import { all, call, put, takeLatest, select, SagaReturnType } from 'redux-saga/effects'
import * as httpStatusCodes from 'http-status-codes'
import get from 'lodash/get'

import ErrorList from '../../../components/shared/modal/error-list/ErrorList'
import {
  CREATE,
  UPDATE,
  SUBMIT,
  EDIT,
  UPLOAD_LOGO,
  setDraftId,
  setDraft,
  setProductOfDraft,
  setDraftStatus,
} from '../../actions/draft/draft'
import {
  FETCH_PACKAGES as FETCH_SUGGESTIBLE_PACKAGES,
  setSuggestiblePackages,
} from '../../actions/packages/packages'
import { fetchJobs } from '../../actions/jobs/jobs'
import {
  showDialog,
  setSubmissionStatusOfDraft,
  setCreationStatusOfDraft,
  setLogoUploadingStatus,
  setJobEditingStatus,
} from '../../actions/ui/ui'
import { initialJobContent, product as initialProduct } from '../../reducers'
import createDraft from '../../../service/draft/create-draft/createDraft'
import updateDraft from '../../../service/draft/update-draft/updateDraft'
import updateJob from '../../../service/draft/update-job/updateJob'
import submitDraft from '../../../service/draft/submit-draft/submitDraft'
import fetchJob from '../../../service/job/fetch-job/fetchJob'
import uploadLogo from '../../../service/draft/upload-logo/uploadLogo'
import jobContentToDraft from '../../../service/util/job-content-to-draft/jobContentToDraft'
import draftToJobContent from '../../../service/util/draft-to-job-content/draftToJobContent'
import navigateToPage from '../../../util/navigate-to-page/navigateToPage'
import imageDimensions from '../../../util/image-dimensions/imageDimensions'
import type {
  CreateDraftAction,
  EditDraftAction,
  SubmitDraftAction,
  UpdateDraftAction,
  UploadLogoAction,
} from '../../actions/draft/draft'
import type { UiStoreType } from '../../reducers/ui/ui'
import type { DashboardUser } from '../../../schema/user'
import type { Job, JobContent } from '../../reducers/jobs/jobs'
import type { Draft, DraftStoreType } from '../../reducers/draft/draft'
import type { JobPostingProduct, JobPostingEntitlements, EntitlementStoreType } from '../../reducers/entitlement/entitlement'
import { canUserTypeAlternativeContactInfo } from '../../selectors/user/user'
import getProducts from '../../../service/products/getProducts'
import * as localStorageUtil from '../../../service/util/localStorage/localStorage'
import { DisplayOption } from '../../../schema/webshop-package'

export const userFromStore = ({
  user,
}: {
  user: DashboardUser,
}): DashboardUser => user

export const uiFromStore = ({ ui }: { ui: UiStoreType }): UiStoreType => ui

export const jobPostingEntitlementsFromStore: ({ entitlements }: { entitlements: EntitlementStoreType}) =>
  JobPostingEntitlements = ({ entitlements }) => entitlements.jobPostings

export const productFromStore = ({
  draft: { product },
}: {
  draft: DraftStoreType,
}): JobPostingProduct => product

export const validationError = (
  message: string,
): { type: string, message: string } => ({
  type: 'validation',
  message,
})

export const formDataWithLogo = (logo: Blob): FormData => {
  const formData = new FormData()
  formData.append('logo', logo)
  return formData
}

export const validationErrorMap: Map<string, string> = new Map([
  ['content.title', 'Vacaturetitel'],
  ['content.description', 'Omschrijving'],
  ['content.functionGroups', 'Functienaam voor matching'],
  ['location.postalCode', 'Postcode'],
  ['applyVia.firstName', 'Voornaam'],
  ['applyVia.lastName', 'Achternaam'],
  ['applyVia.emailAddress', 'E-mailadres'],
])

export function* createDraftSaga({
  payload: { product },
}: CreateDraftAction): Generator<any, void, any> {
  try {
    yield put(setCreationStatusOfDraft(true))

    const user: DashboardUser = yield select(userFromStore)
    const { status, data: id } = yield call(createDraft, product)

    if (status === httpStatusCodes.CREATED) {
      const userCanTypeAlternativeContactInfo = canUserTypeAlternativeContactInfo(
        user,
      )
      const draft: Draft = yield call(jobContentToDraft, {
        jobContent: initialJobContent,
        product: product || initialProduct,
        user,
        userCanTypeAlternativeContactInfo,
        mergeContactInfo: true,
      })

      yield put(setDraft(draft))
      yield put(setProductOfDraft(product || initialProduct))
      yield put(setDraftStatus('draft'))
      yield put(setDraftId(id))
      yield call(
        navigateToPage,
        `/vacature/${id}/informatie`,
      )
    } else {
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content: 'Onverwachte serverreactie ontvangen.',
        }),
      )
    }
  } catch (error) {
    // Sentry.captureException(error)
    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content:
          'De vacature editor is momenteel niet bereikbaar. Probeer het later opnieuw.',
      }),
    )
  }
  yield put(setCreationStatusOfDraft(false))
}

export function* updateDraftSaga({
  payload: { draft, id, status, setSubmitting },
}: UpdateDraftAction): Generator<any, void, any> {
  const isDraft = !status || status.includes('draft')
  try {
    const user: DashboardUser = yield select(userFromStore)
    const product: JobPostingProduct = yield select(productFromStore)
    const userCanTypeAlternativeContactInfo = canUserTypeAlternativeContactInfo(
      user,
    )
    const jobContent: JobContent = yield call(draftToJobContent, {
      draft,
      user,
      userCanTypeAlternativeContactInfo,
    })
    yield put(
      setDraft(
        jobContentToDraft({
          jobContent,
          product,
          user,
          userCanTypeAlternativeContactInfo,
          mergeContactInfo: false,
        }),
      ),
    ) // since the job object is enhanced with some additional data right before its sending to the endpoint, the data has to be double-transformed and saved back to the Redux store, in order for it to be in-sync
    const response = yield call(isDraft ? updateDraft : updateJob, {
      id,
      jobContent,
    })
    if (get(response, 'status') === httpStatusCodes.ACCEPTED) {
      if (isDraft) {
        // When creating a draft and user has job credits, show select credits view, otherwise show suggest products
        const jobPostings: JobPostingEntitlements = yield select(jobPostingEntitlementsFromStore)
        const hasJobPostingEntitlements = jobPostings.length > 0

        if (hasJobPostingEntitlements) {
          // Navigate to select credits
          yield call(navigateToPage,`/vacature/${id}/credits`)
        } else {
          // Navigate to suggest products
          yield call(navigateToPage,`/vacature/${id}/product`)
        }
      } else {
        yield call(navigateToPage, '/vacatures')
      }
    } else {
      /* Sentry.captureException(new Error(`Failed to update ${isDraft ? 'draft' : 'job'} for ${id}
        with jobContent ${JSON.stringify(jobContent)}`))*/
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content: 'Onverwachte serverreactie ontvangen.',
        }),
      )
    }
  } catch (error) {
    if (get(error, 'response.status') === httpStatusCodes.BAD_REQUEST) {
      const errors = yield call(get, error, 'response.data.errors')
      const content = yield errors
        ? call(ErrorList, { errors, map: validationErrorMap })
        : undefined
      yield put(
        showDialog({
          title: 'Er zijn problemen met de invoer',
          content,
        }),
      )
    } else {
      // Sentry.captureException(error)
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content: `${isDraft ? 'Concept' : 'Vacature'} versturen niet gelukt.`,
        }),
      )
    }
  }
  yield call(setSubmitting, false)
}

export function* submitDraftSaga({
  payload: {
    id, product, upgrades,
  },
}: SubmitDraftAction): Generator<any, void, any> {
  try {
    yield put(setSubmissionStatusOfDraft(true))
    const response = yield call(submitDraft, id, product, upgrades)
    if (get(response, 'status') === httpStatusCodes.ACCEPTED) {
      yield call(
        navigateToPage,
        `/vacature/${id}/resultaat`,
      )
      yield put(fetchJobs())
      yield call(localStorageUtil.removeDraft)
    } else {
      /* Sentry.captureException(
        Error(
          `Failed to submitDraft for ${id}.
        Expected ${httpStatusCodes.ACCEPTED} but got ${response.status}.`,
        ),
      )*/
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content: 'Onverwachte serverreactie ontvangen.',
        }),
      )
    }
  } catch (error) {
    const errorMessage = error?.response?.data
    // Sentry.captureException(errorMessage || error, { tags: { jobId: id } })
    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content: errorMessage || 'Conceptindiening is mislukt.',
      }),
    )
  }
  yield put(setSubmissionStatusOfDraft(false))
}

export function* editDraftSaga({
  payload: { id, status, noNavigation },
}: EditDraftAction): Generator<any, void, any> {
  try {
    yield put(setJobEditingStatus(true))
    const response = yield call(fetchJob, id)
    if (get(response, 'status') === httpStatusCodes.OK && response.data) {
      const {
        product,
        content,
        status: statusFromApi,
      }: Job = yield response.data
      const user: DashboardUser = yield select(userFromStore)
      const userCanTypeAlternativeContactInfo = canUserTypeAlternativeContactInfo(
        user,
      )
      const draft: Draft = yield call(jobContentToDraft, {
        jobContent: content || initialJobContent,
        product: product || initialProduct,
        user,
        userCanTypeAlternativeContactInfo,
        mergeContactInfo: false,
      })
      yield put(setDraft(draft))
      yield put(setProductOfDraft(product || initialProduct))
      yield put(setDraftId(id))
      yield put(setDraftStatus(status || statusFromApi?.value))
      if (!noNavigation) {
        yield call(
          navigateToPage,
          `/vacature/${id}/informatie`,
        )
      }
    } else {
      /* Sentry.captureException(
        // eslint-disable-next-line max-len
        Error(`Failed to fetchJob for ${id}. Expected ${httpStatusCodes.OK} but got ${response.status}.`),
      )*/
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content: 'Onverwachte serverreactie ontvangen.',
        }),
      )
    }
  } catch (error) {
    // Sentry.captureException(error)
    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content:
          'De vacature editor is momenteel niet bereikbaar. Probeer het later opnieuw.',
      }),
    )
  }
  yield put(setJobEditingStatus(false))
}

export function* uploadLogoSaga({
  payload: { file, setFieldValue, setFieldError },
}: UploadLogoAction): Generator<any, void, any> {
  try {
    yield put(setLogoUploadingStatus(false))

    const { type, size } = file

    if (!['image/png', 'image/jpeg'].includes(type)) throw validationError('Alleen PNG en JPG zijn toegestaan')
    if (size > 2097152) {
      throw validationError(
        `Het bestand is ${Math.floor((size / 1048576) * 10)
          / 10} MB, de grootte moet kleiner dan 2 MB zijn`,
      )
    }

    const { width } = yield call(imageDimensions, file)
    if (width < 280) {
      throw validationError(
        `De breedte van de afbeelding is ${width} pixels. Minimale breedte moet 280 pixels zijn.`,
      )
    }

    const formData = yield call(formDataWithLogo, file)
    try {
      yield put(setLogoUploadingStatus(true))
      const response = yield call(uploadLogo, formData)
      if (get(response, 'status') === httpStatusCodes.OK && response.data) {
        const { location: key } = response.data
        yield call(setFieldValue, 'logo.key', key, true)
      } else {
        yield put(
          showDialog({
            title: 'Er ging iets mis',
            content: 'Onverwachte serverreactie ontvangen.',
          }),
        )
      }
    } catch (error) {
      // Sentry.captureException(error)
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content:
            'Het uploaden van het logo is mislukt. Probeer het later opnieuw.',
        }),
      )
    }
  } catch (error) {
    if (get(error, 'type') !== 'validation') {
      // Sentry.captureException(error)
    }
    yield call(
      setFieldError,
      'logo.key',
      get(error, 'type') === 'validation'
        ? error.message
        : 'Er ging iets mis met het uploaden',
    )
  }
  yield put(setLogoUploadingStatus(false))
}

export function* fetchSuggestiblePackagesSaga(): Generator<any, void, any> {
  try {
    const response: SagaReturnType<typeof getProducts> = yield call(getProducts, [DisplayOption.combined, DisplayOption.jpf])
    yield put(setSuggestiblePackages(response.data))
  } catch (e) {
    // yield call(Sentry.captureException, e)
    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content: 'Probeer het opnieuw of vernieuw de pagina',
      }),
    )
  }
}

export default function* saga(): Generator<any, void, void> {
  yield all([
    takeLatest(CREATE, createDraftSaga),
    takeLatest(UPDATE, updateDraftSaga),
    takeLatest(SUBMIT, submitDraftSaga),
    takeLatest(EDIT, editDraftSaga),
    takeLatest(UPLOAD_LOGO, uploadLogoSaga),
    takeLatest(FETCH_SUGGESTIBLE_PACKAGES, fetchSuggestiblePackagesSaga),
  ])
}
